import { io, type Socket } from "socket.io-client";

// const env = process.env.NEXT_PUBLIC_NODE_ENV;

const CHAT_SOCKET_URL =
  import.meta.env.VITE_NODE_ENV === "development"
    ? import.meta.env.VITE_API_CHAT_SOCKET_URL_DEV
    : import.meta.env.VITE_NODE_ENV === "stage"
      ? import.meta.env.VITE_API_CHAT_SOCKET_URL_STAGE
      : import.meta.env.VITE_API_CHAT_SOCKET_URL_PROD;

export type ChatSocketAuth = {
  token: string;
  userId: string;
};

let chatSocket: Socket | null = null;
let lastAuthKey: string | null = null;

const connectSubscribers = new Set<() => void>();

function notifyChatSocketConnect() {
  connectSubscribers.forEach((fn) => {
    try {
      fn();
    } catch {
      // ignore subscriber errors
    }
  });
}

/** Re-join rooms or refresh listeners after any successful socket connect/reconnect. */
export function subscribeChatSocketConnect(handler: () => void): () => void {
  connectSubscribers.add(handler);
  return () => {
    connectSubscribers.delete(handler);
  };
}

export function getChatSocket(): Socket | null {
  return chatSocket;
}

export function connectChatSocket(auth: ChatSocketAuth): Socket {
  const authKey = `${auth.userId}:${auth.token}`;

  if (!CHAT_SOCKET_URL) {
    throw new Error("CHAT_SOCKET_URL is not configured");
  }

  if (chatSocket && lastAuthKey === authKey) {
    if (!chatSocket.connected) chatSocket.connect();
    return chatSocket;
  }

  if (chatSocket) {
    try {
      chatSocket.disconnect();
    } catch {
      // ignore
    }
  }

  chatSocket = io(CHAT_SOCKET_URL, {
    transports: ["websocket", "polling"], // Add polling as fallback
    autoConnect: false,
    reconnection: true,
    reconnectionAttempts: 10,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    timeout: 20000,
    withCredentials: true,
    auth: { token: auth.token },
    query: { userId: auth.userId },
  });

  // Add connection error handlers
  chatSocket.on("connect", () => {
    console.log("✅ Chat socket connected");
    notifyChatSocketConnect();
  });

  chatSocket.on("connect_error", (error) => {
    console.error("❌ Chat socket connection error:", error.message);
  });

  chatSocket.on("disconnect", (reason) => {
    console.warn("🔌 Chat socket disconnected:", reason);
    // Reconnect if the disconnection was not intentional
    if (reason === "io server disconnect") {
      // The server disconnected, reconnect manually
      chatSocket?.connect();
    }
  });

  chatSocket.on("reconnect", (attemptNumber) => {
    console.log(`🔄 Chat socket reconnected after ${attemptNumber} attempts`);
    notifyChatSocketConnect();
  });

  chatSocket.on("reconnect_attempt", (attemptNumber) => {
    console.log(`🔄 Chat socket reconnection attempt ${attemptNumber}`);
  });

  chatSocket.on("reconnect_error", (error) => {
    console.error("❌ Chat socket reconnection error:", error.message);
  });

  chatSocket.on("reconnect_failed", () => {
    console.error("❌ Chat socket reconnection failed after all attempts");
  });

  lastAuthKey = authKey;
  chatSocket.connect();
  return chatSocket;
}

export function disconnectChatSocket() {
  if (!chatSocket) return;
  try {
    chatSocket.disconnect();
  } finally {
    chatSocket = null;
    lastAuthKey = null;
  }
}

export function joinChatRoom(roomId: string) {
  if (!chatSocket) return;
  chatSocket.emit("joinRoom", roomId);
}

export function leaveChatRoom(roomId: string) {
  if (!chatSocket) return;
  chatSocket.emit("leaveRoom", roomId);
}

export function sendTypingStatus(roomId: string, isTyping: boolean) {
  if (!chatSocket) return;
  chatSocket.emit("typing", { roomId, isTyping });
}

export function emitMessageRead(roomId: string, messageId: string) {
  if (!chatSocket) return;
  chatSocket.emit("messageRead", { roomId, messageId });
}
